import {
  BILLING_CYCLES,
  PRODUCTS,
  ADDON_PRODUCTS,
  SIGNAL_MANAGER_PRODUCT,
  EA_HOSTING_PRODUCT,
  EA_HOSTING_TRIAL_MAX_SLOTS,
  PRODUCT_NAME_PATTERNS
} from './config.js';

const PRODUCT_NICK = {
  standard: PRODUCTS.standard,
  premium: PRODUCTS.premium,
  futures: PRODUCTS.futures,
  analyzer: ADDON_PRODUCTS.analyzer,
  analyser: ADDON_PRODUCTS.analyzer,
  tc_analyzer: ADDON_PRODUCTS.analyzer,
  dedicated: ADDON_PRODUCTS.dedicated,
  // Backend ships Dedicated Env prices as `dedicatedenv_<cycle>` — without this
  // alias parseNickname returns null, the DE price is never indexed into the
  // catalog, and resolveCatalogPrice(dedicated, cycle) yields nothing → no DE
  // line item → empty plan (CTA disabled, no preview).
  dedicatedenv: ADDON_PRODUCTS.dedicated,
  dedi: ADDON_PRODUCTS.dedicated,
  tc_dedicated: ADDON_PRODUCTS.dedicated,
  tc_dedi: ADDON_PRODUCTS.dedicated,
  // Futures DE. parseNickname tries the LONGEST prefix first, so these win over
  // the plain `futures` seat product below.
  futures_dedicated_env: ADDON_PRODUCTS.dedicatedFutures,
  futures_dedicatedenv: ADDON_PRODUCTS.dedicatedFutures,
  futures_dedicated: ADDON_PRODUCTS.dedicatedFutures,
  futures_dedi: ADDON_PRODUCTS.dedicatedFutures,
  // Signal Manager (whitelabel). Backend nickname is `signal_manager_<cycle>`;
  // the rest are legacy aliases old prices may still carry.
  whitelabel: SIGNAL_MANAGER_PRODUCT,
  wl: SIGNAL_MANAGER_PRODUCT,
  tc_whitelabel: SIGNAL_MANAGER_PRODUCT,
  signal_manager: SIGNAL_MANAGER_PRODUCT,
  signalmanager: SIGNAL_MANAGER_PRODUCT,
  mcp: ADDON_PRODUCTS.mcp,
  // EA Hosting. The live nickname is `ea_connect_<cycle>` (the product is sold
  // as "EA Hosting" but priced under the EA Connect name); the rest are
  // aliases. Keep them all: a nickname with no entry here never indexes into
  // the catalog, and the EA checkout then resolves an empty plan — CTA
  // disabled, no preview (see the dedicatedenv comment above).
  ea_connect: EA_HOSTING_PRODUCT,
  eaconnect: EA_HOSTING_PRODUCT,
  ea_hosting: EA_HOSTING_PRODUCT,
  eahosting: EA_HOSTING_PRODUCT
};

const CYCLE_NICK = {
  monthly: BILLING_CYCLES.monthly,
  quarterly: BILLING_CYCLES.quarterly,
  semiannually: BILLING_CYCLES.semiAnnual,
  semi_annually: BILLING_CYCLES.semiAnnual,
  semiannual: BILLING_CYCLES.semiAnnual,
  annually: BILLING_CYCLES.annual,
  annual: BILLING_CYCLES.annual,
  yearly: BILLING_CYCLES.annual
};

// Catalog "variant" lets the same {product, cycle} carry multiple SKUs whose
// nicknames disambiguate by suffix — currently used for Futures, which has
// both a tiered pay-as-you-go SKU (`futures_<cycle>`) and a flat unlimited SKU
// (`futures_<cycle>_unlimited`). Add more variants here as backend SKUs grow.
export const PRICE_VARIANTS = {
  default: 'default',
  unlimited: 'unlimited'
};

const VARIANT_SUFFIX = {
  unlimited: PRICE_VARIANTS.unlimited
};

/**
 * Map a Stripe product/display name to a product key (Standard's name is
 * "Accounts"). Walks PRODUCT_NAME_PATTERNS in order — premium before standard,
 * because "Accounts Premium" also contains "Accounts".
 */
export function productFromDisplayName(name) {
  const n = String(name || '').toLowerCase();
  if (!n) return null;
  const hit = PRODUCT_NAME_PATTERNS.find(([, re]) => re.test(n));
  return hit ? hit[0] : null;
}

/** Infer product + cycle from a Stripe price display name when nickname is missing. */
export function inferPriceMetaFromName(name) {
  const n = String(name || '').toLowerCase();
  const product = productFromDisplayName(n);
  if (!product) return null;

  let cycle = null;
  for (const [nick, mapped] of Object.entries(CYCLE_NICK)) {
    if (n.includes(nick) || n.includes(nick.replace(/_/g, ' '))) {
      cycle = mapped;
      break;
    }
  }
  if (!cycle) cycle = BILLING_CYCLES.monthly;
  const variant = /(^|[_\s])unlimited(\b|$)/.test(n) ? PRICE_VARIANTS.unlimited : PRICE_VARIANTS.default;
  return { product, cycle, variant };
}

export function parseNickname(nickname) {
  if (!nickname) return null;
  const parts = String(nickname).toLowerCase().split('_');
  if (parts.length < 2) return null;

  // Peel off an optional trailing variant suffix (e.g. `_unlimited`) before
  // looking for the cycle. Without this, `futures_monthly_unlimited` would
  // try to match "unlimited" as the cycle and fail.
  let variant = PRICE_VARIANTS.default;
  let cycleIdx = parts.length - 1;
  if (parts.length >= 3) {
    const tail = parts[parts.length - 1];
    if (VARIANT_SUFFIX[tail]) {
      variant = VARIANT_SUFFIX[tail];
      cycleIdx = parts.length - 2;
    }
  }
  const cycle = CYCLE_NICK[parts[cycleIdx]];
  if (!cycle) return null;

  // The product tokens must consume EVERYTHING before the cycle. Multi-token
  // products (`signal_manager_monthly`) match on their full prefix; falling
  // back to shorter prefixes turned unknown compound SKUs into their base
  // product — `futures_dedicated_env_monthly` (a $25 Dedicated-Env-product
  // price) classified as plain `futures`, hijacked the futures slot in the
  // byKey index, and the checkout planner re-ADDED the already-owned line to
  // the anchor sub on every drawer open (a real overcharge). Unknown
  // compounds must classify as null instead: never selectable for new
  // purchases, while existing lines on them fall back to product-NAME
  // classification (→ dedicated, handled by the DE dedup).
  const product = PRODUCT_NICK[parts.slice(0, cycleIdx).join('_')];
  if (product) return { product, cycle, variant };
  return null;
}

// Loose product/cycle classifier for EXISTING-subscription line items only.
// Tries strict parseNickname, then progressively drops trailing tokens so a
// superseded price renamed with a trailing marker (e.g. `standard_monthly_legacy`)
// still classifies via its base `standard_monthly`. Deliberately NOT used for
// catalog byKey selection (parseNickname stays strict), so a renamed legacy
// price can never become selectable for a NEW purchase.
export function classifyNicknameLoose(nickname) {
  if (!nickname) return null;
  const parts = String(nickname).toLowerCase().split('_');
  for (let end = parts.length; end >= 2; end -= 1) {
    const meta = parseNickname(parts.slice(0, end).join('_'));
    if (meta) return meta;
  }
  return null;
}

/** @param {object} price */
export function priceMeta(price) {
  if (!price) return null;
  return parseNickname(price.nickname) || inferPriceMetaFromName(price.name);
}

function keyOf(product, cycle, variant = PRICE_VARIANTS.default) {
  return `${product}:${cycle}:${variant}`;
}

export function buildPriceCatalog(pricesResponse) {
  const list = Array.isArray(pricesResponse?.prices) ? pricesResponse.prices : Array.isArray(pricesResponse) ? pricesResponse : [];

  const byKey = new Map();
  const byId = new Map();
  for (const price of list) {
    // Index by BOTH the internal UUID (`id`) and the Stripe price ID. Sub line
    // items reference Stripe IDs (`price_xxx`), so callers that look up a price
    // by what's stored on a subscription would otherwise hit a null. Indexing
    // under both keys keeps lookup transparent regardless of which ID the
    // caller has in hand.
    if (price?.id) byId.set(price.id, price);
    if (price?.stripePriceId) byId.set(price.stripePriceId, price);
    // Superseded prices stay resolvable by id (existing subs still reference
    // them for tier-aware cost display) but must never be selectable for new
    // purchases, so they are kept out of the {product,cycle,variant} index.
    if (price?.superseded) continue;
    const meta = priceMeta(price);
    if (!meta) continue;
    const variant = meta.variant || PRICE_VARIANTS.default;
    byKey.set(keyOf(meta.product, meta.cycle, variant), {
      ...price,
      ...meta,
      variant
    });
  }

  return {
    raw: list,
    get(product, cycle, variant = PRICE_VARIANTS.default) {
      return byKey.get(keyOf(product, cycle, variant)) || null;
    },
    has(product, cycle, variant = PRICE_VARIANTS.default) {
      return byKey.has(keyOf(product, cycle, variant));
    },
    getById(id) {
      return byId.get(id) || null;
    },
    listForProduct(product) {
      const out = [];
      for (const entry of byKey.values()) {
        if (entry.product === product) out.push(entry);
      }
      return out;
    }
  };
}

/**
 * Resolve a catalog price for product + billing cycle (+ optional variant),
 * with fallbacks for existing subscribers (cycle from active subscription)
 * and single-price catalogs.
 *
 * @param {ReturnType<typeof buildPriceCatalog>} catalog
 * @param {string} product
 * @param {string} preferredCycle
 * @param {{ subscriptions?: object[], variant?: string }} [options]
 */
export function resolveCatalogPrice(catalog, product, preferredCycle, options = {}) {
  if (!catalog) return null;

  const variant = options.variant || PRICE_VARIANTS.default;

  const cyclesToTry = [];
  if (preferredCycle) cyclesToTry.push(preferredCycle);

  const subs = options.subscriptions || [];
  const primary = subs.find((s) => ['active', 'trialing', 'past_due'].includes(String(s.status ?? s.Status).toLowerCase()));
  if (primary) {
    const nickMeta = parseNickname(primary.priceNickname);
    let subCycle = nickMeta?.cycle ?? null;
    if (!subCycle) {
      const ic = Number(primary.intervalCount ?? primary.interval_count ?? 1);
      const interval = primary.interval;
      if (interval === 'year') subCycle = BILLING_CYCLES.annual;
      else if (interval === 'month') {
        if (ic === 3) subCycle = BILLING_CYCLES.quarterly;
        else if (ic === 6) subCycle = BILLING_CYCLES.semiAnnual;
        else if (ic >= 12) subCycle = BILLING_CYCLES.annual;
        else subCycle = BILLING_CYCLES.monthly;
      } else subCycle = BILLING_CYCLES.monthly;
    }
    if (subCycle && !cyclesToTry.includes(subCycle)) cyclesToTry.push(subCycle);
  }

  for (const cycle of cyclesToTry) {
    const hit = catalog.get(product, cycle, variant);
    if (hit?.id) return hit;
  }

  // Variant requested but not stocked on the preferred cycle → fall back to the
  // default variant on the same cycle rather than skipping straight to "any
  // entry for this product", which could return a wildly different cycle.
  if (variant !== PRICE_VARIANTS.default) {
    for (const cycle of cyclesToTry) {
      const hit = catalog.get(product, cycle, PRICE_VARIANTS.default);
      if (hit?.id) return hit;
    }
  }

  // Same-cycle resolution ONLY. Never substitute a different billing cycle, or
  // the buyer is charged for a cycle they didn't choose (a monthly request must
  // never resolve to the annual price — the "$10 shown, $120 charged" bug). If
  // the requested cycle isn't stocked, return null and let the caller omit the
  // item rather than mis-charge.
  const entries = catalog.listForProduct?.(product) || [];
  for (const cycle of cyclesToTry) {
    const hit = entries.find((e) => e.cycle === cycle && (e.variant || PRICE_VARIANTS.default) === variant);
    if (hit?.id) return hit;
  }
  for (const cycle of cyclesToTry) {
    const hit = entries.find((e) => e.cycle === cycle);
    if (hit?.id) return hit;
  }

  return null;
}

// EA Hosting is sold per bot slot on a flat per_unit price with no included
// bundle, so BM exempts it from the tiered-plan guards; its trial instead
// covers at most EA_HOSTING_TRIAL_MAX_SLOTS. Every other flat price stays
// trial-ineligible (Futures unlimited is flat too).
function isPerSlotTrialPrice(price) {
  return priceMeta(price)?.product === EA_HOSTING_PRODUCT;
}

// Mirrors BM `createSubscriptionWithTrial` eligibility (common.go:813-893):
// every requested price must be tiered (graduated), carry the SAME
// trial_period_days > 0, AND the requested quantity must fit inside the
// first tier's included bundle (`tiers[0].up_to`). Going over the bundle
// pushes the cart past the "trial-included" allotment — BM rejects the
// whole CreateSubscription request with "trial is limited to the included
// accounts", so sending use_trial=true here would surface as a checkout
// error. Frontend never sends trial_period_days itself; backend reads it.
// EA Hosting is the one flat exception (isPerSlotTrialPrice).
export function computeUseTrial(items, catalog, usedProductIds = []) {
  if (!Array.isArray(items) || items.length === 0 || !catalog) return false;
  const used = usedProductIds instanceof Set ? usedProductIds : new Set(usedProductIds || []);
  let baselineDays = 0;
  for (const it of items) {
    const price = catalog.getById?.(it.price_id);
    const days = Number(price?.trial_period_days ?? 0);
    if (!Number.isFinite(days) || days <= 0) return false;
    if (baselineDays === 0) baselineDays = days;
    else if (days !== baselineDays) return false;

    // One-time trial is per product. If the user already consumed it for this
    // product, the cart isn't trial-eligible — mirrors BM HasTrialHistory so the
    // preview total and use_trial flag match what CreateSubscription will do.
    const productId = price?.productId || price?.product_id || '';
    if (productId && used.has(productId)) return false;

    if (isPerSlotTrialPrice(price)) {
      if (Number(it.quantity ?? 0) > EA_HOSTING_TRIAL_MAX_SLOTS) return false;
      continue;
    }
    const tiers = Array.isArray(price?.tiers) ? price.tiers : null;
    if (!tiers || tiers.length === 0) return false;
    const includedBase = Number(tiers[0]?.up_to ?? 0);
    const qty = Number(it.quantity ?? 0);
    if (includedBase > 0 && qty > includedBase) return false;
  }
  return true;
}

// Trial *capability*, ignoring quantity: every item is a trial-enabled tiered
// price with the same trial_period_days. `computeUseTrial` adds the
// qty<=included-base gate on top. Splitting the two lets the UI keep the trial
// choice visible (with the "free trial" option greyed) when the only blocker is
// "too many accounts" — instead of the choice silently vanishing.
export function computeTrialCapable(items, catalog, usedProductIds = []) {
  if (!Array.isArray(items) || items.length === 0 || !catalog) return false;
  const used = usedProductIds instanceof Set ? usedProductIds : new Set(usedProductIds || []);
  let baselineDays = 0;
  for (const it of items) {
    const price = catalog.getById?.(it.price_id);
    const days = Number(price?.trial_period_days ?? 0);
    if (!Number.isFinite(days) || days <= 0) return false;
    if (baselineDays === 0) baselineDays = days;
    else if (days !== baselineDays) return false;

    // Already-used products are not trial-capable: the choice should disappear
    // entirely (not just grey out), since no quantity makes it available again.
    const productId = price?.productId || price?.product_id || '';
    if (productId && used.has(productId)) return false;

    if (isPerSlotTrialPrice(price)) continue;
    const tiers = Array.isArray(price?.tiers) ? price.tiers : null;
    if (!tiers || tiers.length === 0) return false;
  }
  return true;
}

// Public prices API returns money in MAJOR units (dollars), e.g. 29.0 = $29.
// Convert to integer cents so all downstream pricing math stays in cents.
function toCents(major) {
  return Math.round(Number(major ?? 0) * 100);
}

// Generic tiered pricing — walks whatever tiers[] the backend sends, honoring
// the price's tiers_mode. No tier-count or up_to assumptions.
//   • volume    — the ENTIRE quantity is billed at the single tier whose upper
//                 bound the total lands in (e.g. legacy standard: qty 1 → $15,
//                 qty ≥2 → $10/account).
//   • graduated — each unit billed at its tier's unit_amount, plus a tier's
//                 flat_amount once if any unit lands in that tier (default).
function computeTieredAmountCents(tiers, qty, mode) {
  if (!Array.isArray(tiers) || tiers.length === 0 || qty <= 0) return 0;
  // up_to ascending; null ("rest") sorts last.
  const ordered = [...tiers].sort((a, b) => {
    const av = a.up_to == null ? Infinity : Number(a.up_to);
    const bv = b.up_to == null ? Infinity : Number(b.up_to);
    return av - bv;
  });

  if (mode === 'volume') {
    const upper = (t) => (t.up_to == null ? Infinity : Number(t.up_to));
    const tier = ordered.find((t) => qty <= upper(t)) ?? ordered[ordered.length - 1];
    return qty * toCents(tier.unit_amount ?? 0) + toCents(tier.flat_amount ?? 0);
  }

  let remaining = qty;
  let billed = 0;
  let total = 0;
  for (const tier of ordered) {
    if (remaining <= 0) break;
    const upTo = tier.up_to == null ? Infinity : Number(tier.up_to);
    const capacity = upTo - billed;
    if (capacity <= 0) continue;
    const unitsInTier = Math.min(remaining, capacity);
    total += unitsInTier * toCents(tier.unit_amount ?? 0);
    total += toCents(tier.flat_amount ?? 0);
    remaining -= unitsInTier;
    billed += unitsInTier;
  }
  return total;
}

// Price for ANY quantity of a price — tiered or flat per_unit.
export function computeLineAmountCents(price, qty) {
  if (!price || qty <= 0) return 0;
  if (Array.isArray(price.tiers) && price.tiers.length > 0) {
    return computeTieredAmountCents(price.tiers, qty, price.tiers_mode ?? price.tiersMode);
  }
  return toCents(price.unit_amount ?? 0) * qty;
}

// Kept for callers that still reference the futures-specific name.
export const computeFuturesAmountCents = computeLineAmountCents;

// Price of a single unit (first account). For tiered prices this is the real
// cost of qty=1 — derived from the backend tiers, not a guessed field.
export function getUnitAmountCents(price) {
  return computeLineAmountCents(price, 1);
}

// Marginal cost of one more account — computed from the backend tiers, so it
// stays correct for any tier shape (or 0 extras on a flat-rate price).
export function getExtraUnitAmountCents(price) {
  if (!price) return 0;
  return computeLineAmountCents(price, 2) - computeLineAmountCents(price, 1);
}

// Build POST /checkout items[] from selection + catalog. Any count > 0 is included.
export function buildCheckoutItems(selection, catalog, options = {}) {
  const items = [];
  const { billingCycle, standardCount, premiumCount, futuresCount, futuresUnlimited } = selection;
  const baseCatalogOpts = { subscriptions: options.subscriptions || [] };

  if (standardCount > 0) {
    const p = resolveCatalogPrice(catalog, PRODUCTS.standard, billingCycle, baseCatalogOpts);
    if (p) items.push({ price_id: p.id, quantity: standardCount });
  }
  if (premiumCount > 0) {
    const p = resolveCatalogPrice(catalog, PRODUCTS.premium, billingCycle, baseCatalogOpts);
    if (p) items.push({ price_id: p.id, quantity: premiumCount });
  }
  // Unlimited Futures is a flat whole-product SKU — backend models it as a
  // per_unit price with qty=1 (the "1 license = unlimited accounts" idiom).
  // Tiered Futures takes the user-selected account count. The unlimited intent
  // only takes effect when the catalog actually stocks the flat SKU for this
  // cycle; otherwise fall back to the tiered variant (with the real count) so a
  // stale unlimited flag never ships a tiered price billed as a single account.
  const hasUnlimitedFutures = Boolean(catalog?.has?.(PRODUCTS.futures, billingCycle, PRICE_VARIANTS.unlimited));
  const effectiveFuturesUnlimited = Boolean(futuresUnlimited) && hasUnlimitedFutures;
  const wantsFutures = effectiveFuturesUnlimited || futuresCount > 0;
  if (wantsFutures) {
    const p = resolveCatalogPrice(catalog, PRODUCTS.futures, billingCycle, {
      ...baseCatalogOpts,
      variant: effectiveFuturesUnlimited ? PRICE_VARIANTS.unlimited : PRICE_VARIANTS.default
    });
    if (p) {
      items.push({
        price_id: p.id,
        quantity: effectiveFuturesUnlimited ? 1 : futuresCount
      });
    }
  }
  return items;
}
